import { isTenantManagerPlus, type RoleKey } from "@/shared/lib/rbac";

/**
 * 这个人上传知识库文档会发生什么。
 *
 * - `direct`:平台超管代客态 —— 传完直接生效(它本人就是 checker)。
 * - `propose`:租户 MANAGER+ —— 传完落待审,平台超管批了才对外生效(§7.1「放开提名,不放开入库」)。
 * - `none`:没资格传。后端 `knowledge_base.write` = [TENANT_MANAGER, TENANT_ADMIN],
 *   给 STAFF 亮按钮只会让他撞 403。
 *
 * D6 之前这里是 `canManage = isImpersonating` —— 租户完全传不了。那条封条是**因为没有治理门**
 * 才贴的(注释原文:"为保证语料质量,租户侧暂不开放自助上传")。门建好之后再关着,
 * `tenant_proposal` 渠道在生产里就没有任何 UI 入口。
 *
 * ★2026-09-07 — 个人知识库上传**不走这个 gate**。这个函数管的是「传进租户库」这条链路
 * (直传 / 提名待审,门槛 MANAGER+);个人文档(`knowledgeService.uploadPersonalDocument`,
 * `/tenant/knowledge/personal/upload`)员工本人即可传,不经过 `kbUploadMode` 判定,也不落
 * 待审队列——它是另一条完全独立的写路径,详见 `PersonalDocsPanel`。别把这个函数的返回值
 * 套到个人文档 tab 上,那会把员工唯一能写的入口重新锁死。
 */
export type KbUploadMode = "direct" | "propose" | "none";

export function kbUploadMode(
  impersonating: boolean,
  role: RoleKey | null | undefined,
): KbUploadMode {
  // 代客要先判:代客 token 的 role 是 PLATFORM_ADMIN,按角色走会误判成 none
  if (impersonating) {
    return "direct";
  }
  return isTenantManagerPlus(role) ? "propose" : "none";
}

/**
 * 1006 — 提名态横幅里「谁来审」那一句。审批规则(api `KbDocumentReviewService`):
 * 提名人 ≠ 审批人;**例外**:租户里除我之外没有其他启用的管理员(`review-policy` 回 `soloApprover=true`)
 * 时,唯一管理员可以自审。此前横幅一律写「提名人与审批人不能是同一人」,与单管理员自审矛盾。
 *
 * - `isAdmin=false`(经理上传):由管理员审,与自审无关;
 * - `soloApprover=undefined`(口径还没拿到 / 拿失败):不下结论,只说谁能审;
 * - `true` / `false`:按规则如实说。
 */
export function kbProposeReviewerHint(isAdmin: boolean, soloApprover: boolean | undefined): string {
  if (!isAdmin) {
    return "由企业管理员在「审核台」审核,也可由平台运营审核。";
  }
  if (soloApprover === true) {
    return "你是本企业唯一的管理员,可在「审核台」直接审批自己上传的文档,也可由平台运营审核。";
  }
  if (soloApprover === false) {
    return "管理员可在「审核台」审核;你自己上传的需由另一位管理员或平台运营审批(四眼原则)。";
  }
  return "管理员可在「审核台」审核,也可由平台运营审核。";
}
